/**
 * Documentation Navigation Utility
 *
 * @description
 * Builds and exports the navigation tree structure for the documentation feature.
 * Maps the actual documentation directory structure to a user-friendly navigation hierarchy.
 *
 * The navigation tree is used by the DocumentationSidebar component to render
 * collapsible category sections and individual document links.
 */

/**
 * Interface for a navigation item
 */
export interface NavItem {
  /** Display title for the navigation item */
  title: string;
  /** File path relative to /docs directory (e.g., 'getting-started/introduction.md') */
  path: string;
  /** Child navigation items for nested structure */
  children?: NavItem[];
}

/**
 * Main navigation tree for documentation
 *
 * This structure matches the actual files in /docs directory:
 * - getting-started/
 * - core-concepts/
 * - research-design/
 * - publishing-sharing/
 * - results-analysis/
 * - participant-experience/
 * - account-management/
 * - FAQ.md
 */
export const navigationTree: NavItem[] = [
  {
    title: 'Getting Started',
    path: 'getting-started/README.md',
    children: [
      {
        title: 'Introduction',
        path: 'getting-started/introduction.md',
      },
      {
        title: 'Account Setup',
        path: 'getting-started/account-setup.md',
      },
      {
        title: 'First Project',
        path: 'getting-started/first-project.md',
      },
    ],
  },
  {
    title: 'Core Concepts',
    path: 'core-concepts/README.md',
    children: [
      {
        title: 'Organizations',
        path: 'core-concepts/organizations.md',
      },
      {
        title: 'Workspaces',
        path: 'core-concepts/workspaces.md',
      },
      {
        title: 'Projects',
        path: 'core-concepts/projects.md',
      },
      {
        title: 'Roles & Permissions',
        path: 'core-concepts/roles-permissions.md',
      },
    ],
  },
  {
    title: 'Research Design',
    path: 'research-design/README.md',
    children: [
      {
        title: 'Instructions: Quick Start',
        path: 'research-design/instructions-quickstart.md',
      },
      {
        title: 'Instructions: Response Types',
        path: 'research-design/instructions-response-types.md',
      },
      {
        title: 'Instructions: Tone & Language',
        path: 'research-design/instructions-tone-language.md',
      },
      {
        title: 'Instructions: Advanced',
        path: 'research-design/instructions-advanced.md',
      },
      {
        title: 'Discussion Guide: Basics',
        path: 'research-design/discussion-guide-basics.md',
      },
      {
        title: 'Discussion Guide: Writing Questions',
        path: 'research-design/discussion-guide-writing-questions.md',
      },
      {
        title: 'Discussion Guide: Probing',
        path: 'research-design/discussion-guide-probing.md',
      },
      {
        title: 'Discussion Guide: Media',
        path: 'research-design/discussion-guide-media.md',
      },
      {
        title: 'Discussion Guide: Examples',
        path: 'research-design/discussion-guide-examples.md',
      },
      {
        title: 'Discussion Guide: Dynamic Variables',
        path: 'research-design/discussion-guide-dynamic-variables.md',
      },
      {
        title: 'Context Training: Quick Start',
        path: 'research-design/context-training-quickstart.md',
      },
      {
        title: 'Context Training: Complete Guide',
        path: 'research-design/context-training-complete.md',
      },
      {
        title: 'Context Training: Examples',
        path: 'research-design/context-training-examples.md',
      },
      {
        title: 'Screening: Basics',
        path: 'research-design/screening-questions-basics.md',
      },
      {
        title: 'Screening: Logic',
        path: 'research-design/screening-questions-logic.md',
      },
      {
        title: 'Screening: Examples',
        path: 'research-design/screening-questions-examples.md',
      },
      {
        title: 'Max Participants',
        path: 'research-design/max-participants.md',
      },
    ],
  },
  {
    title: 'Publishing & Sharing',
    path: 'publishing-sharing/README.md',
    children: [
      {
        title: 'Overview',
        path: 'publishing-sharing/README.md',
      },
      {
        title: 'Publishing Requirements',
        path: 'publishing-sharing/publishing-requirements.md',
      },
      {
        title: 'Publishing Process',
        path: 'publishing-sharing/publishing-process.md',
      },
      {
        title: 'Sharing Options',
        path: 'publishing-sharing/sharing-options.md',
      },
      {
        title: 'Panel Integration',
        path: 'publishing-sharing/panel-integration.md',
      },
      {
        title: 'Survey Embedding',
        path: 'publishing-sharing/survey-embedding.md',
      },
    ],
  },
  {
    title: 'Results & Analysis',
    path: 'results-analysis/README.md',
    children: [
      {
        title: 'Overview',
        path: 'results-analysis/README.md',
      },
      {
        title: 'Analysis Workflow',
        path: 'results-analysis/analysis-workflow.md',
      },
      {
        title: 'Transcripts',
        path: 'results-analysis/transcripts.md',
      },
      {
        title: 'Questions & Insights',
        path: 'results-analysis/questions-insights.md',
      },
      {
        title: 'AI Search',
        path: 'results-analysis/ai-search.md',
      },
      {
        title: 'Reports',
        path: 'results-analysis/reports.md',
      },
      {
        title: 'Exporting Data',
        path: 'results-analysis/exporting-data.md',
      },
    ],
  },
  {
    title: 'Participant Experience',
    path: 'participant-experience/README.md',
    children: [
      {
        title: 'Interview Flow',
        path: 'participant-experience/interview-flow.md',
      },
      {
        title: 'Participant Journey',
        path: 'participant-experience/participant-journey.md',
      },
      {
        title: 'Participant FAQs',
        path: 'participant-experience/participant-faqs.md',
      },
      {
        title: 'Participant Troubleshooting',
        path: 'participant-experience/participant-troubleshooting.md',
      },
      {
        title: 'Technical Requirements Summary',
        path: 'participant-experience/technical-requirements-summary.md',
      },
      {
        title: 'Technical Requirements: Devices',
        path: 'participant-experience/technical-requirements-devices.md',
      },
      {
        title: 'Technical Requirements: Troubleshooting',
        path: 'participant-experience/technical-requirements-troubleshooting.md',
      },
      {
        title: 'Technical Requirements: Advanced',
        path: 'participant-experience/technical-requirements-advanced.md',
      },
    ],
  },
  {
    title: 'Account Management',
    path: 'account-management/README.md',
    children: [
      {
        title: 'Profile Settings',
        path: 'account-management/profile-settings.md',
      },
      {
        title: 'Team Management',
        path: 'account-management/team-management.md',
      },
    ],
  },
  {
    title: 'FAQ',
    path: 'FAQ.md',
  },
];

/**
 * Helper function to get all navigation paths as a flat array
 * Useful for validation and testing
 *
 * @returns Array of all document paths in the navigation tree
 */
export function getAllDocPaths(): string[] {
  const paths: string[] = [];

  function extractPaths(items: NavItem[]) {
    items.forEach((item) => {
      if (item.path) {
        paths.push(item.path);
      }
      if (item.children) {
        extractPaths(item.children);
      }
    });
  }

  extractPaths(navigationTree);
  return paths;
}

/**
 * Helper function to find a navigation item by path
 *
 * @param path - The document path to search for
 * @returns The matching NavItem or undefined if not found
 */
export function findNavItemByPath(path: string): NavItem | undefined {
  function searchItems(items: NavItem[]): NavItem | undefined {
    return items.reduce<NavItem | undefined>((found, item) => {
      if (found) return found;
      if (item.path === path) return item;
      if (item.children) return searchItems(item.children);
      return undefined;
    }, undefined);
  }

  return searchItems(navigationTree);
}

/**
 * Helper function to get the category title for a given path
 *
 * @param path - The document path
 * @returns The category title or undefined
 */
export function getCategoryForPath(path: string): string | undefined {
  function searchCategory(items: NavItem[], parent?: NavItem): string | undefined {
    return items.reduce<string | undefined>((found, item) => {
      if (found) return found;
      if (item.path === path && parent) return parent.title;
      if (item.children) return searchCategory(item.children, item);
      return undefined;
    }, undefined);
  }

  return searchCategory(navigationTree);
}
